/**
 * MarketingLink — renders an internal path via react-router's <Link> (client
 * navigation) and an external/absolute URL via a plain <a>. Used by every
 * marketing block + the nav/footer so links behave correctly without each
 * call-site branching. react-router-dom is a peer dependency of @inferrex/ui.
 */
import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface MarketingLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  readonly href: string;
  readonly children?: ReactNode;
}

function isExternal(href: string): boolean {
  return /^(https?:)?\/\//.test(href) || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('#');
}

export function MarketingLink({ href, children, ...rest }: MarketingLinkProps) {
  if (isExternal(href)) {
    const external = /^(https?:)?\/\//.test(href);
    return (
      <a href={href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <Link to={href} {...rest}>
      {children}
    </Link>
  );
}
